<template>
  <div class="full-width document-list-item">
    <div class="document-list-item-toggle" @click="toggle">
      <div class="document-list-item-content">
        <QCheckbox v-if="props.showCheckboxes" v-model="internalChecked" color="secondary" />
        <div class="document-list-item-content-right" :style="{ '--component-width': colWidth }">
          <div class="document-list-item-date">{{ JavascriptHelpers.trimString(document.date, responsiveTextLimit) }}</div>
          <div class="document-list-item-filename">
            {{ JavascriptHelpers.trimString(document.label, responsiveTextLimit) }}
          </div>
          <div v-if="document.amount" class="document-list-item-amount" :class="toggled ? '' : 'ellipsis'">
            {{ NumericHelpers.formatNumberAsCurrency({ number: document.amount }) }}
          </div>
        </div>
      </div>
      <div class="document-list-item-icon">
        <Icon :name="arrowIcon" type="fas" :size="1.3" color="primary" class="q-pa-md" />
      </div>
    </div>
    <template v-if="toggled">
      <div class="document-list-item-actions q-gutter-sm">
        <QBtn
          type="a"
          color="secondary"
          no-caps
          icon-right="fad fa-download"
          :label="t('CORE.DOCUMENTS.ACTIONS.@DOWNLOAD')"
          class="q-py-sm q-px-md"
          outline
          rounded
          elevated
          dense
          @click="downloadDocument"
        />
        <QBtn
          type="a"
          color="secondary"
          no-caps
          icon-right="fad fa-eye"
          :label="t('CORE.DOCUMENTS.ACTIONS.@VIEW')"
          class="q-py-sm q-px-md mdi-box-shadow"
          outline
          rounded
          elevated
          dense
          @click="viewDocument"
        />
      </div>
    </template>
    <ExtracadabraModal v-if="opened" v-model:opened="opened" :header="{ title: document.label }" maximized @close="toggle">
      <template #body>
        <div class="flex full-width full-height justify-center items-center">
          <template v-if="document.extension === FileExtension.pdf.extension">
            <PDF :src="documentFile" :pdf-width="pdfWidth" />
          </template>
          <template v-else>
            <img :src="documentFile" alt="document" width="auto" height="auto" style="max-width: 100%; max-height: calc(100vh - 100px)" />
          </template>
        </div>
      </template>
    </ExtracadabraModal>
  </div>
</template>

<script setup>
  import { useI18n } from "vue-i18n";
  import { Icon } from "@extracadabra/ui-kit";
  import { useQuasar } from "quasar";
  import { defineProps } from "vue";
  import { computed, ref, inject } from "vue";
  import { isMobile } from "@core/plugins/mobile";
  import { DOCUMENTS_MANAGER_POSTHOG_EVENTS } from "@core/constants/common";
  import { downloadBase64Zip, DownloadDocument, FileExtension, DocumentEvent } from "@extracadabra/core-v2";

  import { NotificationsManager } from "@core/managers/notifications.manager";
  import { LoadingManager } from "@core/managers/loading.manager";
  import { TrackingManager } from "@core/managers/tracking.manager";
  import { PosthogManager } from "@core/managers/posthog.manager";
  import { ExtracadabraModal } from "../../modals";
  import { EncodingHelpers, NumericHelpers, JavascriptHelpers } from "@core/helpers";
  import PDF from "pdf-vue3";

  const emit = defineEmits(["update:checked"]);
  const { t } = useI18n();
  const $q = useQuasar();
  const { documentManagerRepository } = inject("repositories");
  const { capacitorDownloadPlugin } = inject("plugins");

  const props = defineProps({
    app: {
      type: String,
      required: true,
    },
    document: {
      type: Object,
      required: true,
    },
    checked: {
      type: Boolean,
      default: false,
    },
    showCheckboxes: {
      type: Boolean,
      default: false,
    },
    nbColumns: {
      type: Number,
      default: 2,
    },
  });

  const opened = ref(false);
  const toggled = ref(false);
  const documentFile = ref(null);
  const arrowIcon = computed(() => (toggled.value ? "arrow-up" : "arrow-down"));
  const pdfWidth = computed(() => ($q.screen.lt.md ? "100%" : "80%"));
  const colWidth = computed(() => {
    return `${100 / props.nbColumns}%`;
  });
  const internalChecked = computed({
    get() {
      return props.checked;
    },
    set() {
      emit("update:checked", !props.checked);
    },
  });

  const filename = computed(() => `${props.document.label}.${props.document.extension}`);
  const responsiveTextLimit = computed(() => {
    // toggled item
    if (toggled.value) {
      return null;
    }
    // large screen
    if ($q.screen.gt.sm) {
      return 30;
    }
    // medium screen
    if ($q.screen.gt.xs) {
      return 18;
    }
    // small screen
    return 13;
  });

  const toggle = () => {
    toggled.value = !toggled.value;
  };
  const downloadDocument = async () => {
    if (!documentFile.value) {
      await getDocument();
    }
    // then do something with the document url

    if (isMobile) {
      try {
        await capacitorDownloadPlugin.downloadFile(documentFile.value, filename.value);
        TrackingManager.addEvent({
          event: DocumentEvent.DOWNLOAD_DOCUMENT,
          params: props.document.type,
        });
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.DOWNLOAD_DOCUMENT,
          type: props.document.type,
        });
        NotificationsManager.success({ message: t("CORE.DOCUMENTS.@DOWNLOAD_SUCCESS") });
      } catch (error) {
        NotificationsManager.error();
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.DOWNLOAD_DOCUMENT_ERROR,
          type: props.document.type,
        });
      }
    } else {
      try {
        downloadBase64Zip(documentFile.value, props.document.label, props.document.extension);
        TrackingManager.addEvent({
          event: DocumentEvent.DOWNLOAD_DOCUMENT,
          params: props.document.type,
        });
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.DOWNLOAD_DOCUMENT,
          type: props.document.type,
        });
        NotificationsManager.success({ message: t("CORE.DOCUMENTS.@DOWNLOAD_SUCCESS") });
      } catch (error) {
        NotificationsManager.error();
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.DOWNLOAD_DOCUMENT_ERROR,
          type: props.document.type,
        });
      }
    }
  };
  const viewDocument = async () => {
    if (!documentFile.value) {
      try {
        await getDocument();
        TrackingManager.addEvent({
          event: DocumentEvent.VIEW_DOCUMENT,
          params: props.document.type,
        });
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.VIEW_DOCUMENT,
          type: props.document.type,
        });
      } catch (error) {
        NotificationsManager.error();
        PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
          name: DocumentEvent.VIEW_DOCUMENT_ERROR,
          type: props.document.type,
        });
      }
    }
    // then do something with the document url
    opened.value = true;
  };

  const getDocument = async () => {
    try {
      LoadingManager.show();
      const blob = await new DownloadDocument(documentManagerRepository).execute(props.document.id, props.app, props.document.type);
      const file = await EncodingHelpers.getBase64FromFile(blob);

      documentFile.value = file;
    } catch (error) {
      NotificationsManager.error();
      PosthogManager.captureEvent(DOCUMENTS_MANAGER_POSTHOG_EVENTS, {
        name: DocumentEvent.GET_DOCUMENT_ERROR,
        type: props.document.type,
      });
    } finally {
      LoadingManager.hide();
    }
  };
</script>

<style lang="scss" scoped>
  // override pdf-vue3 styles
  .pdf-vue3-main {
    width: 100%;
    height: calc(100vh - 100px) !important;
  }
  .document-list-item {
    margin-top: 12px;
    &-actions {
      display: flex;
      flex: 1;
      flex-direction: row-reverse;
      align-items: flex-end;
      padding-top: 12px;
      .q-btn {
        box-shadow: $card-shadow;
      }
    }
    &-toggle {
      background-color: white;
      display: flex;
      border-radius: $button-border-radius;
      box-shadow: $card-shadow;
      cursor: pointer;
    }
    &-content {
      display: flex;
      flex: 1;
      &-right {
        display: flex;
        align-items: center;
        flex: 1;
        > div {
          width: var(--component-width);
          text-align: center;
          word-break: break-all;
          @media (max-width: $breakpoint-xs-max) {
            font-size: 0.9rem;
          }
        }
      }
    }
  }
</style>
